import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { useActionAssigneeDirectory } from 'components/IAP/hooks/useActionAssigneeDirectory';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import {
  mapResourceSetToEditPayload,
  useResourceSets,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { NULL_VALUE } from 'constants/displayConstants';
import type { ResourceRow } from './ResourceTabCard';

type TransferTaconAssignmentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: ResourceRow | null;
  incidentId: string | null | undefined;
  requestCreatorUserGuid?: string | null;
  requestorPosition?: string | null;
  onTransferred: (patch: {
    resourceId: string;
    tacon: string;
    incidents: string[];
  }) => void;
};

function userOptionLabel(userName: string, positionNames: string[]): string {
  const seats =
    positionNames.length > 0 ? positionNames.join(', ') : 'No position';
  return `${userName} - ${seats}`;
}

function defaultTaconId(args: {
  creatorUserGuid?: string | null;
  requestorPosition?: string | null;
  roster: {
    userId: string;
    incidentPositions: { positionId: string; positionName: string }[];
  }[];
}): string | null {
  const { creatorUserGuid, requestorPosition, roster } = args;
  if (!creatorUserGuid) return null;
  const creator = roster.find((member) => member.userId === creatorUserGuid);
  if (!creator) return creatorUserGuid;
  const seats = creator.incidentPositions || [];
  if (seats.length === 0) return creator.userId;
  const wanted = requestorPosition?.trim().toLowerCase();
  if (wanted) {
    const match = seats.find(
      (seat) => seat.positionName?.trim().toLowerCase() === wanted
    );
    if (match?.positionId) return match.positionId;
  }
  return seats[0]?.positionId || creator.userId;
}

export function TransferTaconAssignmentDialog({
  open,
  onOpenChange,
  row,
  incidentId,
  requestCreatorUserGuid,
  requestorPosition,
  onTransferred,
}: TransferTaconAssignmentDialogProps) {
  const { data: resourceSets = [] } = useResourceSets();
  const resourceSetId = row?.resource_set_id || row?.id || '';
  const updateResourceSet = useUpdateResourceSet(resourceSetId);
  const directory = useActionAssigneeDirectory(INCIDENT, incidentId);
  const incident = useIncident();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const userTouchedRef = useRef(false);

  const positionsByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const member of directory.roster ?? []) {
      map.set(
        member.userId,
        (member.incidentPositions || [])
          .map((seat) => seat.positionName)
          .filter(Boolean)
      );
    }
    return map;
  }, [directory.roster]);

  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of directory.users) {
      map.set(
        user.value,
        userOptionLabel(user.label, positionsByUserId.get(user.value) ?? [])
      );
    }
    for (const position of directory.positions) {
      map.set(position.value, position.label);
    }
    return map;
  }, [directory.positions, directory.users, positionsByUserId]);

  const workspaceName = incident.data?.name || NULL_VALUE;

  useEffect(() => {
    if (!open) {
      userTouchedRef.current = false;
      setSelectedId(null);
      return;
    }
    if (userTouchedRef.current) return;
    const next = defaultTaconId({
      creatorUserGuid: requestCreatorUserGuid,
      requestorPosition,
      roster: directory.roster ?? [],
    });
    if (next) setSelectedId(next);
  }, [directory.roster, open, requestCreatorUserGuid, requestorPosition]);

  const close = () => {
    userTouchedRef.current = false;
    setSelectedId(null);
    onOpenChange(false);
  };

  const confirm = async () => {
    if (!row || !selectedId || !incidentId || !resourceSetId) return;
    const existing = resourceSets.find(
      (rs) =>
        rs.resource_set_id === resourceSetId ||
        rs.id === resourceSetId ||
        rs.id === row.id
    );
    if (!existing) return;

    await updateResourceSet.mutateAsync({
      ...mapResourceSetToEditPayload(resourceSetId, existing),
      tacon: selectedId,
      incidents: [incidentId],
    });

    onTransferred({
      resourceId: row.id || resourceSetId,
      tacon: selectedId,
      incidents: [incidentId],
    });
    close();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          userTouchedRef.current = false;
          setSelectedId(null);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Transfer TACON & Assignment
            {row?.name ? ` — ${row.name}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:rounded-md tw:border tw:border-border tw:bg-muted/40 tw:px-3 tw:py-2 tw:text-sm">
            <span className="tw:font-medium">Incident:</span>{' '}
            <span data-testid="transfer-tacon-incident">{workspaceName}</span>
            <div className="tw:mt-1 tw:text-xs tw:text-muted-foreground">
              On confirm, this asset&apos;s Incident field will be set to this
              workspace.
            </div>
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <div className="tw:text-xs tw:font-medium tw:text-muted-foreground">
              TACON
            </div>
            <Combobox
              value={selectedId}
              itemToStringLabel={(id: string) => labelById.get(id) ?? id}
              onValueChange={(value) => {
                userTouchedRef.current = true;
                setSelectedId(typeof value === 'string' ? value : null);
              }}
            >
              <ComboboxInput
                placeholder="Search users or positions…"
                className="tw:w-full"
              />
              <ComboboxContent className="dark" positionerClassName="tw:z-[70]">
                <ComboboxEmpty>No matches</ComboboxEmpty>
                <ComboboxList>
                  {directory.users.length > 0 ? (
                    <ComboboxGroup>
                      <ComboboxLabel>Users</ComboboxLabel>
                      {directory.users.map((user) => (
                        <ComboboxItem key={user.value} value={user.value}>
                          {labelById.get(user.value) ?? user.label}
                        </ComboboxItem>
                      ))}
                    </ComboboxGroup>
                  ) : null}
                  {directory.positions.length > 0 ? (
                    <ComboboxGroup>
                      <ComboboxLabel>Positions</ComboboxLabel>
                      {directory.positions.map((position) => (
                        <ComboboxItem
                          key={position.value}
                          value={position.value}
                        >
                          {position.label}
                        </ComboboxItem>
                      ))}
                    </ComboboxGroup>
                  ) : null}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              !selectedId ||
              !incidentId ||
              !resourceSetId ||
              updateResourceSet.isPending
            }
            onClick={() => void confirm()}
          >
            Confirm Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
